import { apiClient } from '@/api/client';

/**
 * Initialize Web Push notifications.
 * Call after the user logs in. Safe to call multiple times — it's idempotent.
 *
 * Flow:
 * 1. Check browser support
 * 2. Wait for service worker to be ready
 * 3. Request notification permission
 * 4. Subscribe to push with server's VAPID public key
 * 5. Send subscription to backend
 */
export async function initPushNotifications(): Promise<void> {
  try {
    // 1. Check browser support
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('[Push] Not supported in this browser');
      return;
    }

    // 2. Wait for service worker
    const registration = await navigator.serviceWorker.ready;

    // 3. Request notification permission (no-op if already granted/denied)
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('[Push] Notification permission denied');
      return;
    }

    // 4. Get VAPID public key from server
    const { data } = await apiClient.get<{ publicKey: string }>(
      '/api/notifications/vapid-public-key'
    );
    if (!data.publicKey) {
      console.warn('[Push] Server has no VAPID key configured');
      return;
    }

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Convert VAPID key from base64url to Uint8Array
      const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      console.log('[Push] New subscription created');
    } else {
      console.log('[Push] Existing subscription found');
    }

    // 5. Send subscription to backend
    await apiClient.post('/api/notifications/push-token', {
      token: JSON.stringify(subscription),
      platform: 'web',
    });

    console.log('[Push] Subscription registered with server');
  } catch (err) {
    console.warn('[Push] Init failed:', err);
  }
}

/**
 * Unsubscribe from Web Push (call on logout).
 */
export async function unsubscribePush(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) return;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      // Remove from server
      try {
        await apiClient.delete('/api/notifications/push-token', {
          data: { token: JSON.stringify(subscription) },
        });
      } catch {}
      // Unsubscribe locally
      await subscription.unsubscribe();
      console.log('[Push] Unsubscribed');
    }
  } catch (err) {
    console.warn('[Push] Unsubscribe failed:', err);
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
